/**
 * Aug 2026 · Android / Chrome install chip.
 *
 * Wires the standard `beforeinstallprompt` event Chrome / Edge / Samsung
 * Internet fire when the app becomes installable. iOS Safari never
 * fires this event — its counterpart is `IosInstallBanner`.
 *
 * Flow:
 *   1. Chrome sniffs the manifest + service-worker + fetch handler.
 *   2. Once install criteria pass, browser fires `beforeinstallprompt`.
 *      We `preventDefault()` to suppress the mini-infobar and stash the
 *      deferred event.
 *   3. We render a small blue chip in the corner. Tap → we call
 *      `deferredPrompt.prompt()` and await the user's choice.
 *   4. If the user installs (or dismisses hard), the browser fires
 *      `appinstalled` — we hide the chip and remember the choice in
 *      `localStorage` so we don't nag on the next visit.
 */
import { useEffect, useState } from "react";
import { DownloadSimpleIcon, XIcon } from "@phosphor-icons/react";

const DISMISS_KEY = "mes.pwa.android_prompt_dismissed";

export default function AndroidInstallChip() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      // Already installed? Skip.
      if (typeof window !== "undefined" && window.matchMedia
          && window.matchMedia("(display-mode: standalone)").matches) return;
      // User has already dismissed us? Skip.
      if (localStorage.getItem(DISMISS_KEY) === "1") return;
    } catch (_e) { /* ignore */ }

    const onBIP = (e) => {
      e.preventDefault();  // suppress the default mini-infobar
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      try { localStorage.setItem(DISMISS_KEY, "1"); } catch (_e) {}
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice && choice.outcome === "accepted") {
        setVisible(false);
      } else {
        // User dismissed — remember it so the chip doesn't reappear.
        try { localStorage.setItem(DISMISS_KEY, "1"); } catch (_e) {}
        setVisible(false);
      }
    } catch (_e) {
      setVisible(false);
    } finally {
      setDeferred(null);
    }
  };

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch (_e) {}
  };

  if (!visible || !deferred) return null;

  return (
    <div
      data-testid="android-install-chip"
      className="fixed left-3 right-3 bottom-3 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[320px] z-50 rounded-2xl border border-[#007AFF]/40 bg-[var(--bg-1)]/95 backdrop-blur shadow-2xl p-3"
    >
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#007AFF] flex items-center justify-center font-bold text-white text-xs">NF</div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-white text-sm">Install Narrow Fabric</div>
          <div className="text-[11px] text-zinc-400 leading-tight">Add the MES to your Android home screen</div>
        </div>
        <button
          data-testid="android-install-btn"
          onClick={install}
          className="h-8 px-3 rounded-md bg-[#007AFF] hover:bg-blue-600 text-white text-xs font-semibold flex items-center gap-1"
        ><DownloadSimpleIcon size={12} weight="bold" /> Install</button>
        <button
          data-testid="android-install-dismiss"
          onClick={dismiss}
          className="text-zinc-500 hover:text-white p-1"
          aria-label="Dismiss install prompt"
        ><XIcon size={12} weight="bold" /></button>
      </div>
    </div>
  );
}
